import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { RefreshCw, CheckCircle2, XCircle, AlertCircle, Clock } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface CronJobRow {
  jobid: number;
  jobname: string;
  schedule: string;
  active: boolean;
  command: string | null;
  runid: number | null;
  status: string | null;
  return_message: string | null;
  start_time: string | null;
  end_time: string | null;
}

const statusBadge = (status: string | null) => {
  if (!status) {
    return (
      <Badge variant="outline" className="gap-1">
        <Clock size={12} /> No runs (7d)
      </Badge>
    );
  }
  if (status === "succeeded") {
    return (
      <Badge variant="outline" className="gap-1 border-emerald-500/40 text-emerald-600 bg-emerald-500/10">
        <CheckCircle2 size={12} /> Succeeded
      </Badge>
    );
  }
  if (status === "failed") {
    return (
      <Badge variant="destructive" className="gap-1">
        <XCircle size={12} /> Failed
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className="gap-1">
      <AlertCircle size={12} /> {status}
    </Badge>
  );
};

const CronJobsPanel = () => {
  const [rows, setRows] = useState<CronJobRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error } = await supabase.rpc("get_all_cron_jobs_with_latest_run" as never);
    if (error) {
      setError(error.message);
      setRows([]);
    } else {
      setRows((data as CronJobRow[]) ?? []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const failedCount = rows.filter((r) => r.status === "failed").length;
  const inactiveCount = rows.filter((r) => !r.active).length;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2">
            Cron Jobs
            {failedCount > 0 && (
              <Badge variant="destructive">{failedCount} failed</Badge>
            )}
            {inactiveCount > 0 && (
              <Badge variant="secondary">{inactiveCount} inactive</Badge>
            )}
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            All scheduled jobs with the status of their most recent run (last 7 days).
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchRows} disabled={loading}>
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          <span className="ml-2">Refresh</span>
        </Button>
      </CardHeader>
      <CardContent>
        {error && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive mb-4">
            {error}
          </div>
        )}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Job</TableHead>
                <TableHead>Schedule</TableHead>
                <TableHead>Active</TableHead>
                <TableHead>Last status</TableHead>
                <TableHead>Last run</TableHead>
                <TableHead>Message</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    Loading…
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    No cron jobs found.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow
                    key={r.jobid}
                    className={r.status === "failed" ? "bg-destructive/5" : undefined}
                  >
                    <TableCell className="font-medium">{r.jobname}</TableCell>
                    <TableCell>
                      <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{r.schedule}</code>
                    </TableCell>
                    <TableCell>
                      {r.active ? (
                        <Badge variant="outline" className="border-emerald-500/40 text-emerald-600">
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Paused</Badge>
                      )}
                    </TableCell>
                    <TableCell>{statusBadge(r.status)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {r.start_time
                        ? `${formatDistanceToNow(new Date(r.start_time))} ago`
                        : "—"}
                    </TableCell>
                    <TableCell className="max-w-md">
                      {r.return_message ? (
                        <span className="text-xs text-muted-foreground line-clamp-2" title={r.return_message}>
                          {r.return_message}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default CronJobsPanel;
